// screens/Clark/answer/AuthorityCard.jsx — BUNDLE 2 · STEP 4. THE ONE AUTHORITY RENDERER.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// 🔴 WHY THIS IS AN EXTRACTION AND NOT A NEW CARD, IN ONE PARAGRAPH
//
// ge5 caught the composer saying "not yet analyzed" while the authorities panel said "no negative
// treatment detected" — about THE SAME CASE, IN THE SAME RESPONSE. Two layers, two vocabularies,
// one contradiction, shipped. The instruction for this step was therefore explicit: do NOT
// hand-build a second card, because a second implementation guarantees that recurs.
//
// So this file is where the authority vocabulary lives, once, and every surface that names an
// authority consumes it:
//
//   · ResearchAnswer.jsx  — the inline panel under the prose
//   · TemplatedAnswer.jsx — the same panel, plus its wide-tier `<table class="cc-tpl-table">`
//   · SourcesColumn       — the right-hand column this step adds
//
// 🔴 ONE HONEST QUALIFICATION, BECAUSE THE STRONGER CLAIM WOULD BE FALSE. The wide-tier TABLE in
// TemplatedAnswer is still a table, and it should be — collapsing a three-column comparison into
// stacked cards would be a regression in the tier that exists to be scanned. What is unified is
// not the LAYOUT but the two things that can actually contradict each other: the treatment
// VOCABULARY (every surface renders `TreatmentPill`, whose only vocabulary is `TREATMENT_COPY`,
// fed by `goodLawTier()` from @citeclerk/goodlaw) and the LINK (`researchHref`, here).
// `clarkAuthorityParity.test.js` pins that: no file under screens/Clark may contain a treatment
// phrase except parts.jsx. A fourth surface can add a layout; it cannot add a word.
//
// 🔴 IT NEVER INVENTS A STATE STRING. `TREATMENT_COPY` in parts.jsx is the only vocabulary, and
// the flag reaching it is folded by `goodLawTier()` from @citeclerk/goodlaw — the shared package
// extracted precisely so this rule is not implemented twice. When Bundle 1 changes what a treatment
// state is CALLED, it changes here and lands on every surface in the same commit. Divergence stops
// being a thing anyone has to remember not to do.
//
// 🔴 THE DEEP LINK IS BY CLUSTER ID, NEVER BY CAPTION. `Jones v. State` is four different cases.
// `caseId` is the CourtListener cluster id the engine already returns on every authority
// (clarkAnswerShape.js:125), and it is the only identifier here that identifies anything.

import { goodLawTier } from "@citeclerk/goodlaw";
// 🔴 STREAM C — THE CARD ITSELF IS NOW @citeclerk/ui's. This file keeps everything that is about
// CLARK'S DATA — the deep link by cluster id, the relied/retrieved split, the empty states — and
// stops owning the markup, which is the half that had zero properties in common with Research's.
// `TreatmentPill` is no longer imported: the merged card has no treatment slot, deliberately, and
// leaving the import here would make re-adding one a one-line accident. `pillFlag` below is
// exported and still consumed by index.jsx, so the fold itself is untouched.
import SharedAuthorityCard from "@citeclerk/ui/AuthorityCard";
import { seeAlsoLabel } from "@citeclerk/ui/seeAlsoLabel"; // RUN SH · SH-3 — see the block above `RailItem`
// 🔴 RUN RB — THE RAIL CARD NO LONGER LEAVES CLARK. See SourcesColumn at the foot of this file for
// where the state lives and why it lives there rather than in index.jsx.
import { useCallback, useRef, useState } from "react";
import AuthorityModal from "@citeclerk/ui/AuthorityModal";

// 🔴 THE FOLD MOVED HERE FROM ResearchAnswer.jsx, and it had to. It is the function that
// turns an engine status into one of the four words the UI is allowed to say, so it belongs
// with the renderer that says them — not in one of the three surfaces that consume it. The
// Sources column needed it too, and importing it from a sibling ANSWER component would have
// made a layout component the owner of the vocabulary.
/**
 * Canonical status → the four pill keys parts.jsx knows.
 *
 * 🔴 TWO TIERS parts.jsx DOES NOT HAVE A KEY FOR, AND WHY THEY MAP THE WAY THEY DO:
 *
 *   'verified-good' — the STRONGEST clear state (a verified source said so). parts.jsx's fallback
 *     would have sent it to `pending` and rendered "Treatment check pending" on the one case we are
 *     most confident about — a real defect, caught by folding through the canonical function
 *     instead of matching on strings. It maps to `no-known-negative`, whose copy is "No negative
 *     treatment detected." That is also the ONLY thing we are allowed to say: the UI language
 *     contract forbids "good law", because the engine cannot grant that from an absence.
 *   'checking' — a sweep is in flight. `pending` is exactly right, and note Clark never CAUSES one:
 *     this path is cache-read-only (sweep:false), so a 'checking' here belongs to somebody else's
 *     request.
 */
export function pillFlag(goodLaw) {
  // 🔴 A5.9/10 — ABSENT IS NOT "PENDING". With the GoodLaw citator off the server sends no
  // `goodLaw` field at all, and the honest rendering of that is NOTHING — no pill, no words. The
  // old `!goodLaw?.status -> "pending"` collapsed two different states into one and would have
  // told a user "not yet analyzed" about a check that is never going to run, which is a promise
  // rather than a status.
  //   goodLaw === undefined/null  -> we do not make this claim  -> null (render nothing)
  //   goodLaw = {status:'pending'} -> the check ran, no verdict  -> "pending"
  if (goodLaw == null) return null;
  if (!goodLaw.status) return "pending";            // absence of a STATUS is never a clearance
  const tier = goodLawTier(goodLaw.status, { source: goodLaw.source });
  if (tier === "negative") return "negative";
  if (tier === "caution") return "caution";
  if (tier === "no-known-negative" || tier === "verified-good") return "no-known-negative";
  return "pending";                                  // 'checking', 'pending', anything unrecognized
}



export { researchHref } from "./authorityLink.mjs";
import { researchHref } from "./authorityLink.mjs";

/**
 * ONE authority, rendered.
 *
 * `authority` = { id, caseId, type, cite, role, treatment, relied }
 * `variant`   = 'panel' (inline, under the prose) | 'column' (the right-hand sources rail)
 *
 * The variants differ in LAYOUT ONLY — same fields, same vocabulary, same link, same pill. A
 * variant that dropped the pill would be the divergence this file exists to prevent, just spelled
 * differently, so there is no branch here that omits anything.
 */
export function AuthorityCard({ authority, variant = "panel", onOpenResearch }) {
  const href = researchHref(authority);
  const isColumn = variant === "column";
  const year = authority?.date ? String(authority.date).slice(0, 4) : null;

  // 🔴 STREAM C — "Retrieved; not relied on for this answer." IS A STATUS, NOT A REASON.
  // `clarkAnswerShape.js:141` substitutes that sentence into `role` when nothing in the answer
  // cited the authority. As Clark's own "why this was relied on" line it was self-contradicting,
  // and on the merged card it would sit in the slot reserved for what an authority ESTABLISHED.
  // The card is already grouped under a "Related authorities" heading that says the same thing once.
  const why = authority?.role && !/^Retrieved; not relied on/.test(authority.role)
    ? authority.role
    : null;

  /* ══ 🔴 RUN PJ — THE RAIL CARD CARRIES NO BODY TEXT AND NO BADGE. ADAM'S RULING, 2026-08-25. ═══
     A rail card is a cite and a meta line. Nothing else.

     ⭐ AND IT IS THE FIX FOR THE MIS-ATTRIBUTION, BY DELETION RATHER THAN BY REPAIR. `why` is not
     a per-authority holding — it is THE SENTENCE OF THE ANSWER THAT NAMED THIS AUTHORITY, and a
     sentence routinely names several. MEASURED on the shipped renderer against RUN PY's captured
     Kennedy v. Bremerton turn (ops/run-pj/shots/before-1440.png, ops/run-pj/out/probe-before.json):
     Garcetti's card and Pickering's card carried the SAME 476-character sentence, and so did
     Lemon's and Town of Greece's — clamped to three lines, so each one broke off mid-clause while
     appearing to state that case's holding. Everson's card carried a sentence naming *Trinity
     Lutheran Church v. Comer's*, an authority that block's own callout says is NOT IN OUR CORPUS.

     🔴 SAY ONLY WHAT WAS MEASURED. This is the MECHANISM of the McDonald's-carrying-Heller's-
     sentence report — one sentence, N authorities, printed N times. The specific instance Adam
     saw came from v1's envelope, which no longer exists, and RUN NB and RUN NA both failed to
     reproduce THAT instance in v2. What ships here is the removal of the surface it lived on.
     No claim is made that a bug which did not reproduce has been fixed.

     🔴 FENCED TO THE COLUMN, DELIBERATELY. `variant="panel"` is `AuthorityList`, which
     ResearchAnswer.jsx and TemplatedAnswer.jsx render under the prose — surfaces RUN PJ does not
     own. The ruling is about the rail; the panel is untouched and still passes both fields.

     ⭐ THIS IS ALSO WHY NO packages/ui EDIT WAS NEEDED. `why` and `type` are prop-guarded in the
     shared card (`{why ? … : null}`, `{type || statusLabel ? … : null}`), so declining to pass
     them removes them here and changes nothing on Research. See STATUS.md, "the packages/ui
     decision". */
  /* Named for what it IS, not for `isColumn` — and deliberately NOT `stripped`, which in this
     codebase means the authorities the corpus does not hold (`envelope.stripped`). Two meanings for
     one word in one answer surface is how the next reader loses ten minutes. */
  const bareCard = isColumn;

  return (
    <SharedAuthorityCard
      /* 🔴 RUN VC — IN THE RAIL THE CARD IS NO LONGER THE LIST ITEM. Adam's change #4 puts a
         `Statute` / `Case` eyebrow ABOVE each card; the eyebrow and the card together are one
         entry, so the `<li>` is the pair and the card is a `<div>` inside it. `as` is a prop the
         shared component already takes (`'li' (default) | 'div'`, packages/ui/react/AuthorityCard
         .jsx:68) for exactly this — no packages/ui edit, and Research, which passes nothing, still
         gets its `<li>`. The panel variant is untouched: `AuthorityList` still renders bare cards
         into its own `<ul>` and they are still the list items there. */
      as={isColumn ? "div" : "li"}
      legacyClass="cc-ans-card"
      testId="clark-authority-card"
      data-variant={variant}
      /* ══ 🔴🔴 RUN CR · BLOCK 7 — THE SOURCE CHIP REACHES THE INLINE PANEL TOO. ═══════════════
         ADAM: *"The authorities for Clark need to be in cards and need to have the source chips.
         Needs to look 1:1 with Research except for no highlights in Clark."*

         MEASURED FIRST, on the rendered DOM of both Clark surfaces (ops/run-cr/clark/out-panel.json):
           rail  card  `.cc-auth__type` → "Case", bg rgba(182,31,31,.10), 4px radius, 1px border
           PANEL card  `.cc-auth__type` → "CASE", bg TRANSPARENT, 0 radius, 0 border, grey #63625b
         The CARD was never the defect — both surfaces already render `@citeclerk/ui`'s `.cc-auth`
         with a white fill, a 1px edge and a 10px radius. The CHIP was: RUN VA's skin is selected
         through `.cc-clark-sources-item[data-kind]`, which is the RAIL's `<li>` class, so the
         inline panel fell through to the shared component's quiet grey uppercase label.

         ⭐ THE FIX IS RUN RD · R7's TECHNIQUE, WHICH IS THE ONE THIS REPO ALREADY USES FOR EXACTLY
         THIS: put `data-kind` on the card and let a stylesheet skin it. `data-kind` rides on
         `...rest`, which the shared card already spreads onto its root — nothing is added to
         `packages/ui/react/AuthorityCard.jsx`, which this run does not edit and which is frozen.
         The `<li>` in the rail keeps its own `data-kind` and its own rule; this adds the attribute
         to the CARD, which is the element both surfaces have in common.

         🔴 THE HUE IS A FUNCTION OF THE DOCUMENT TYPE AND OF NOTHING ELSE, which is the safety
         argument RUN VA made and RUN RD · R7 restated: every case is the same red on every row of
         every answer whatever its treatment, so a red-tinted chip beside a case name cannot be
         read as a citator verdict. Keying off the TYPE rather than off the printed word is what
         keeps that true. */
      data-kind={/statute/i.test(String(authority?.type || authority?.kind || "")) ? "statute" : "case"}
      name={authority?.name || null}
      cite={authority?.cite || null}
      /* 🔴🔴 RUN VA — ADAM OVERRULED THE PLACEMENT: THE TYPE GOES INSIDE THE CARD, AT THE TOP.
         "I want the authority type at the top INSIDE the card. Not above the card." 2026-08-25.
         RUN VC withheld `type` here and rendered a separate eyebrow ABOVE the card (see RailItem),
         reasoning that keeping it out preserved RUN PJ's ruling that the rail card carries a cite
         and a meta line and nothing else. That was VC's judgement and Adam has replaced it; PJ's
         ruling was Adam's to move.
         ⭐ NO packages/ui EDIT WAS NEEDED, WHICH IS THE POINT OF THE SLOT. The shared card already
         renders `.cc-auth__typeline > .cc-auth__type` as the FIRST child of the card body — top,
         inside — and has since RUN S. The eyebrow existed because this line withheld the prop, not
         because the card had nowhere to put it. Research (apps/steel) is untouched byte for byte. */
      type={authority?.type}
      court={authority?.court || null}
      year={year}
      /* 🔴 ADAM RULED THE CITING COUNT STAYS, AND THE RAIL CANNOT DRAW IT TODAY — the reason is
         upstream of this file and is NOT a defect in it. `index.jsx`'s rail mapping (:1160-1176)
         passes no `citationCount`, no `court` and no `date`, so all three are `undefined` here and
         the shared card's `Number.isFinite(citedBy) && citedBy > 0` guard renders nothing.
         MEASURED: 0 `.cc-auth__sig` elements in the rendered rail, 6 cards. The line is left
         exactly as it is — it is correct code reading a field nobody sends — and the mapping spec
         is handed to RUN PN in STATUS.md. ✅ Adam's worry that a zero would print "Cited by 0" does
         not occur: the `> 0` guard already covers it. */
      citedBy={Number.isFinite(authority?.citationCount) ? authority.citationCount : undefined}
      why={bareCard ? undefined : why}
      href={href || undefined}
      // 🔴 BUNDLE E · LANE 2's finding is PRESERVED: in the rail the whole card is the link, not a
      // repeated "Open in Research →" line under every one of five cards. It survives because the
      // shared card has exactly one hit area and no inner link — which is also what closes STEP 0
      // §0.4's older bug, where the control was a <button> whose handler no production caller
      // passed, so it was inert on every path. An <a> with a real href cannot be inert.
      onSelect={onOpenResearch ? () => onOpenResearch(authority) : undefined}
      unlinkedNote={
        // No cluster id means no honest link. The card still renders every fact it has and says
        // plainly why it does not open — better than a link that opens the WRONG case, because a
        // caption is not an identifier and this is the one place that could pretend it is.
        "Not linked to an opinion record"
      }
      selected={Boolean(authority?.selected)}
    />
  );
}

/* ══ 🔴 RUN RM — THE AUTHORITIES ON A PHONE. ══════════════════════════════════════════════════
 *
 * Spec: `design/mobile-authority-rail-mockup.html`.
 *
 * THE DEFECT THIS REPLACES, MEASURED ON THE SERVED PAGE BEFORE ANYTHING CHANGED. Below 900px
 * `.cc-clark-main` becomes a column (answer.css) and the rail stacks UNDER the composer as a
 * bounded, scrollable strip (`max-height: 40vh`, answer.css). On a phone that strip lands on the
 * footer's line and its own content does not fit inside it:
 *
 *     width   rail bottom   content bottom   hidden by the rail's own scroll
 *     360         750            792                    62px
 *     375         750            777                    46px
 *     390         750            772                    41px
 *     414         750            772                    41px
 *
 * So the proof the answer is grounded — the thing this product exists to show — was a half-cut
 * card below the composer, on the device most strangers arrive on.
 *
 * 🔴 THE FIX IS THE CARDS' POSITION IN THE FLOW, NOT THEIR SIZE. They render INSIDE this control's
 * border, in the thread, so they scroll with the conversation and there is no bounded box left to
 * clip them. Nothing here shrinks anything to make it fit.
 *
 * ⚠️ THE CARD TREATMENT IS THE RAIL'S OWN — `RailItem` inside `ul.cc-ans-panel-list
 * .cc-clark-sources-list`, the exact element and classes `SourcesTurn` renders. Not a second
 * card: the same one, so the phone cannot drift from the desktop as either changes.
 *
 * Above the phone breakpoint this renders nothing at all (CSS `display: none`), and the rail is
 * untouched. Below it, the rail is hidden and this stands in its place.
 */
/* ══ 🔴🔴 RUN CX — THE PHONE HAD NO SEE ALSO AT ALL, AND IT WAS NOT SMALL. IT WAS ABSENT. ════════
 *
 * MEASURED, not inferred: `answer.css:1277-1281` sets
 * `@media (max-width: 599.98px) { .cc-clark-sources { display: none; } }`, so below 600px the rail —
 * Clark's ONLY See-also group — is `display:none`, box 0x0, `body.innerText.length === 0`. This
 * component stands in its place and its props were `{ authorities, onOpenResearch }`: it was never
 * handed `seeAlso` and had no branch that could draw it. Every unverified authority Clark named was
 * invisible on a phone while the same answer showed all of them at 1440.
 *
 * ⭐ FIXED HERE, NOT BY UNHIDING THE RAIL. The rail is a desktop layout — a fixed-width column with
 * its own scroll box — and RUN CX's own harness image (`*-rail-FORCED-VISIBLE-*.png`) is what shows
 * that forcing it onto 390 is wrong. The mobile component is the surface; it needed the data.
 *
 * 🔴 THE SECTION STRUCTURE MATCHES THE RAIL. THE CARD TREATMENT STILL SEPARATES THEM.
 * Both sections use the same `cc-clark-sources-subhead` heading and the same list class, so they
 * read as one control with two parts. What does NOT flatten is the card: AUTHORITIES rows go
 * through `RailItem` (white card, blue citation, `<a tabindex=0>`, `cursor: pointer`) and SEE ALSO
 * rows go through `SeeAlsoCardList` — THE SAME COMPONENT THE RAIL USES — which paints the grey
 * ground, the black citation, `<div tabindex="-1">` and `cursor: default`. Measured at 1440 in this
 * run: 10.49:1 blue-on-white against 16.99:1 black-on-grey. Reusing the rail's own list is what
 * makes "mobile keeps the distinction" true by construction rather than by a second stylesheet.
 *
 * 🔴 EACH SECTION RENDERS ONLY IF IT HAS A ROW, and if both are empty the component returns null
 * and leaves nothing behind — no heading, no chevron, no empty box. */
export function MobileAuthorities({ authorities = [], seeAlso = [], onOpenResearch }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const n = authorities.length;
  const m = seeAlso.length;
  /* The control has to say what it will open, and after this change that is two different kinds of
     thing. A single "N authorities" over a see-also-only answer would be a count that names the
     wrong noun — the rail calls these "See also" precisely because they are NOT authorities we
     hold. Both numbers, or whichever one exists. */
  const label = n && m ? `${n === 1 ? "1 authority" : `${n} authorities`} · ${m} see also`
    : n ? (n === 1 ? "1 authority" : `${n} authorities`)
    : (m === 1 ? "1 see also" : `${m} see also`);

  /* 🔴 EXPANDING MUST SHOW WHAT IT EXPANDED. MEASURED, NOT ASSUMED: the control sits near the
     bottom of the thread's scroll box, so at 390 the first card opened BELOW the fold and a
     stranger who tapped "1 authority" saw a half-card and had to work out that it scrolls. The
     cards were never clipped — `scrollIntoView` is what makes that true to the eye as well as to
     the geometry.
     `block: "nearest"` so a control already fully visible does not jump, and rAF because the cards
     are laid out in the commit that follows this state change — scrolling in the same tick scrolls
     to the collapsed height. Guarded: `scrollIntoView` is absent in some embedded webviews. */
  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) {
        requestAnimationFrame(() => {
          try { boxRef.current?.scrollIntoView({ block: "nearest" }); } catch { /* no-op */ }
        });
      }
      return next;
    });
  };

  // 🔴 RUN CX — BOTH counts, or the see-also-only answer (R-1: 0 relied, 3 see also) renders
  // nothing on a phone while showing three cards at 1440.
  if (!n && !m) return null;
  return (
    <div className="cc-authmob" data-testid="clark-authorities-mobile" ref={boxRef}>
      <button
        type="button"
        className="cc-authmob-btn"
        aria-expanded={open}
        aria-controls="cc-authmob-cards"
        onClick={toggle}
        data-testid="clark-authorities-mobile-toggle"
      >
        {/* 🔴 DERIVED, NEVER HARDCODED — and both readings have to be right. "1 authority",
            "3 authorities". The count is the claim the control makes before anything is tapped. */}
        <span>{label}</span>
        <span className="cc-authmob-chev" aria-hidden="true">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="cc-authmob-cards" id="cc-authmob-cards">
          {/* 🔴 RUN CX — AUTHORITIES ABOVE SEE ALSO, as at desktop: what the product can show comes
              first. Each section is guarded on its own length, so neither heading can appear over
              an empty list. The headings only appear when BOTH sections exist — a lone section
              needs no label to tell it apart from the other one, and the rail does the same. */}
          {n > 0 && (
            <>
              {m > 0 && <h3 className="cc-clark-sources-subhead" data-testid="clark-authmob-subhead-relied">Authorities</h3>}
              <ul className="cc-ans-panel-list cc-clark-sources-list" role="list"
                  data-testid="clark-authorities-mobile-list">
                {authorities.map((a) => (
                  <RailItem key={a.id} authority={a} onOpenResearch={onOpenResearch} />
                ))}
              </ul>
            </>
          )}
          {m > 0 && (
            <>
              {n > 0 && <h3 className="cc-clark-sources-subhead" data-testid="clark-authmob-subhead-seealso">See also</h3>}
              {/* THE RAIL'S OWN LIST COMPONENT. Not a mobile copy — `SeeAlsoCardList` is what draws
                  the grey, black, un-clickable card at 1440, and sharing it is what stops the two
                  widths drifting into describing the same row differently. */}
              <SeeAlsoCardList rows={seeAlso} qId="cc-authmob-cards" listTestId="clark-authmob-seealso"
                               cardTestId="clark-authmob-seealso-card" variant="seealso" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The inline panel — what `ResearchAnswer` and `TemplatedAnswer` render under the prose.
 * Unchanged in markup from the panel it replaces, so no CSS moved.
 */
export function AuthorityList({ authorities = [], onOpenResearch, heading = "Authorities" }) {
  if (!authorities.length) return null;
  return (
    <aside className="cc-ans-panel" aria-label={heading} data-testid="clark-authorities">
      <h2 className="cc-ans-panel-head">{heading}</h2>
      <ul className="cc-ans-panel-list" role="list">
        {authorities.map((a) => (
          <AuthorityCard key={a.id} authority={a} variant="panel" onOpenResearch={onOpenResearch} />
        ))}
      </ul>
    </aside>
  );
}

/**
 * 🔴 STEP 4 — THE SOURCES COLUMN. The right-hand rail: what this answer relied on.
 *
 * Two things it does that the inline panel does not, and both are about the column's job:
 *
 *  1. IT SPLITS RELIED-ON FROM RETRIEVED. `clarkAnswerShape.js:104` already computes `relied` —
 *     whether any verified sentence actually cited this authority. The inline panel lists
 *     everything flat, which is right beneath prose; a column headed "Sources" that mixed the two
 *     would claim the answer rests on cases it merely looked at. The retrieved-but-unused ones are
 *     still SHOWN, under their own heading, because "here is what I looked at and did not use" is
 *     information and hiding it would be the more flattering lie.
 *
 *  2. IT HAS A REAL EMPTY STATE. An answer that relied on nothing gets a sentence saying so, not a
 *     blank rail. A column that renders nothing is indistinguishable from a column that broke —
 *     the same no-blanks rule ResearchAnswer.jsx is built on, applied to the rail.
 */
/**
 * 🔴 BUNDLE C · DEFECT 2 — THE RAIL SAYS WHICH TURN IT IS ABOUT, AND LETS YOU MOVE BETWEEN THEM.
 *
 * A single rail cannot represent a conversation. Without this header it silently re-pointed at the
 * newest answer while the user was reading an older one — the authorities on screen belonged to a
 * question that was no longer the one in view, and nothing said so.
 *
 * `checkedThrough` is the citator's as-of date FOR THIS ANSWER. On a restored turn it comes from the
 * saved snapshot and is deliberately not refreshed: the record states what we knew when we answered.
 */
/**
 * 🔴 BUNDLE UI · LANE 2 — ONE BLOCK PER TURN. THE ← → PAGING IS GONE.
 *
 * Bundle C was right that a single rail cannot represent a conversation: it silently re-pointed at
 * the newest answer while the user was reading an older one. Its fix was an "Answer N of M" pager.
 * This keeps everything that fix established — the per-turn grouping, the question echo, the frozen
 * `checkedThrough` — and drops the pager, because a rail that must be NAVIGATED to match the thread
 * is still a rail that can disagree with the thread.
 *
 * 🔴 RUN VC — LANE 2's SECOND HALF IS REVERSED BY ADAM, 2026-08-25, AND ITS FIRST HALF IS NOT.
 * Lane 2 stacked every answered turn, newest at the BOTTOM, and reasoned that the rail would then
 * "simply scroll with" the conversation. It does not scroll with anything: nothing scrolls the rail
 * when a turn is appended. MEASURED on three real captured turns at 1440x1100
 * (ops/run-vc/out/probe-before.json) — the rail's scroll box is 1100px tall over 1717px of content,
 * `scrollTop` is 0, and the NEWEST turn's block starts at y=1135, thirty-five pixels below the
 * bottom edge. The authorities for the question just asked were off-screen, and the ones on screen
 * belonged to the first question of the session. That is Lane 2's own stated failure, arrived at
 * from the other direction.
 *
 * So: **the latest turn's authorities, alone.** `SourcesColumn` still RECEIVES every answered turn —
 * it has to, to be able to show any of them — and renders exactly one. What Lane 2 was protecting
 * survives intact and is now stronger: the question a block belongs to is written above it, and
 * there is no second block for a reader to confuse it with.
 *
 * `checkedThrough` is the citator's as-of date FOR THIS ANSWER. On a restored turn it comes from
 * the saved snapshot and is deliberately not refreshed: the record states what we knew when we
 * answered.
 */
/* ══ 🔴 RUN VC · CHANGE 4 — ONE RAIL ENTRY: THE EYEBROW, THEN THE CARD. ════════════════════════
   ⭐ THE EYEBROW IS A DOCUMENT TYPE, NOT A STATUS. Adam, 2026-08-25. It says what the document IS —
   a statute or a case — and says nothing whatever about how later courts have treated it. That
   distinction is the reason it is allowed to ship at all: Adam's treatment ruling keeps citator
   verdicts off this rail, and a label that could be read as one would walk straight through it.

   ⭐ IT SITS ABOVE THE CARD, NOT INSIDE IT, WHICH IS WHY IT DOES NOT REOPEN RUN PJ's RULING. PJ
   ruled the rail CARD carries no body text and no badge — *a cite and a meta line, nothing else* —
   and the card still does not: `type` is still withheld from the shared component in the column
   variant (`type={bareCard ? undefined : …}` above). The eyebrow is a label on the list ITEM.

   `kind` is already on the envelope row (clarkAnswerV2.js:342) and `railPayload` folds it to the
   display word once, so all three resolved groups and the see-also group get the same word from
   the same line. This component exists so the eyebrow cannot be added to two groups and forgotten
   on the third. */
function RailItem({ authority, onOpenResearch }) {
  /* 🔴 `data-kind` MOVES FROM THE DELETED EYEBROW ONTO THE ITEM, AND IT IS LOAD-BEARING NOW.
     It was decorative on the eyebrow. It is what colours the chip: the shared `.cc-auth__type`
     span carries no per-type class, so the rail's stylesheet selects on the ITEM's kind
     (`.cc-clark-sources-item[data-kind="statute"] .cc-auth__type`). Doing it this way means the
     chip is a CLARK skin over a shared component — no class added to packages/ui, nothing for
     Research or Library to inherit. See answer.css. */
  return (
    <li className="cc-clark-sources-item" data-kind={authority.kind || undefined}>
      <AuthorityCard authority={authority} variant="column" onOpenResearch={onOpenResearch} />
    </li>
  );
}

/* ══ ✅ RUN SH · SH-3 — THE NEVER-BARE RULE MOVED TO `packages/ui/seeAlsoLabel.mjs`. ════════════
   RUN VC wrote it here; RUN RC wrote a second copy into `apps/steel/.../SeeAlsoCards.jsx` and
   recorded the duplication rather than hiding it. It is one module now, imported at the top of this
   file, and the rule — three shapes, no fourth, PJ's Caron measurement, why the guard tests the
   POSITIVE value — is documented once, there.

   🔴🔴 AND THE COPY THAT LIVED HERE WAS THE UNSAFE ONE. It read `s.citeSource === 'model_asserted'`
   where steel's read `(s.citeSource ?? 'model_asserted') === 'model_asserted'`. On a row with a
   cite, no name, and NO `citeSource` field, this version fell past the attributed branch into
   `return { text: s.cite }` — **the bare volume and page**, which is the one thing the function
   exists to prevent. 75 such rows are sitting in the archives right now.

   ⭐ IT NEVER REACHED THIS SCREEN, AND THAT IS THE POINT, NOT THE DEFENCE. `railPayload.js` writes
   `citeSource: s.citeSource ?? "model_asserted"` before the rail ever sees a row, so Clark was
   correct on the painted DOM by a default in a DIFFERENT FILE, while the function that is supposed
   to be the guard would have printed it bare. Measured: 0 of 135 rail-mapped rows disagree, 75 of
   135 raw-envelope rows do. The shared module takes steel's reading and restores the guard here.
   apps/main/ops/run-sh/out/agree-before.json. */
/* ══ 🔴🔴 RUN VC · DEFECT ZERO — THE QUESTION IS UNCONDITIONAL, AND THAT IS THE FIX. ════════════
   REPORTED, 2026-08-25: on Adam's second question the rail's heading still read *"What is the
   standard for a Rule 12(b)(6) dismissal?"* while the block beneath it held the racial-discrimination
   authorities. A reader attributes the wrong authorities to the wrong question, on a product whose
   whole claim is that it does not do that.

   🔴 THE REPORTED MECHANISM WAS WRONG, AND MEASURING IT IS WHAT FOUND THE REAL ONE. The brief says
   *"the rail is prepending question-blocks and the heading is stuck on the first."* It is not:
   `index.jsx` passes `turns.filter((t) => t.answer)` in thread order and the rail APPENDS, oldest
   at the top. Measured on three real captured turns (ops/run-vc/out/probe-before.json): three
   blocks, each carrying its own question, in order. There is no prepend and there is no stuck
   heading to find.

   🔴 WHAT IS ACTUALLY THERE IS WORSE, BECAUSE IT IS SILENT. The question line was written
   `{q && <p …>}`. **The guard fails OPEN.** A turn whose envelope carries no `q` renders its
   authorities with NO question above them — and because the blocks are stacked in one scroll, the
   nearest heading above those cards is the PREVIOUS question's. Driven, with `q` removed from turn
   2 of the same three real captures (ops/run-vc/out/probe-before-noq.json,
   ops/run-vc/shots/before-noq-rail.png): the heading reads *"Summarize the opinion in Kennedy v.
   Bremerton School District"* and four preliminary-injunction authorities — Winter, Nken, Mazurek,
   Sampson — sit under it with nothing between them. That is the reported artifact, exactly.

   ⭐ AND IT IS THE SHAPE THIS FAMILY KEEPS PAYING FOR: **absence of an answer is not an answer.**
   The heading did not fail loudly and disappear; it failed CALMLY and let the heading above it
   stand in. On a citation surface the calm failure is the one that reaches a brief.

   SO THE HEADING IS NO LONGER CONDITIONAL. Two changes, and the second is why the first is enough:
     1. The line ALWAYS renders. With no `q` it says so, in words, in its own voice — a stated
        absence cannot be read as the previous question.
     2. `SourcesColumn` renders ONE turn (Adam's change #5), so there is no block above to inherit
        from even if something else goes wrong. The binding is also written into the accessibility
        tree: every group in the block is `aria-labelledby` the question's own id, so the
        association is a fact about the DOM rather than a fact about the layout.
   ⭐ Asserted, not assumed: ops/run-vc/verify.mjs drives three questions and fails the run unless
   the rendered heading is the rendered block's own question, on every turn, in both directions. */
function SourcesTurn({ turn, onOpenResearch }) {
  const { id, q, checkedThrough, restored, streaming, scopeLabel } = turn;
  const authorities = turn.authorities || [];
  /* 🔴 RUN SH · SH-1 — TWO ARRAYS, AND THE COMPONENT NEVER SEES THEM AS ONE. `railPayload.js`
     used to `.concat` them; see the block above the groups for what that let the rail say. */
  const seeAlso = turn.seeAlso || [];
  const unchecked = turn.unchecked || [];

  /* The one id that binds this block's heading to every group of cards inside it. */
  const qId = `cc-rail-q-${id}`;

  // 🔴 BUNDLE C · DEFECT 3 — "NOT RELIED ON" AND "NOT YET KNOWN" ARE DIFFERENT, AND THE SPLIT USED
  // TO CONFLATE THEM. Mid-stream the server cannot know what the answer rests on (no sentence has
  // been synthesised yet), so it sends `reliedKnown: false` rather than a `relied: false` this
  // column would have filed under the retrieved group — a claim about an answer that does not exist.
  const known = authorities.filter((a) => a.reliedKnown !== false);
  const unknown = authorities.filter((a) => a.reliedKnown === false);
  const relied = known.filter((a) => a.relied);
  const looked = known.filter((a) => !a.relied);

  return (
    <section
      className="cc-clark-sources-turn"
      data-testid="clark-sources-turn"
      data-turn-id={id}
      /* The question this block is about, on the block itself. It is what ops/run-vc/verify.mjs
         compares the rendered heading against — an invariant a probe can read off the DOM rather
         than infer from the layout. */
      data-q={q || ""}
      aria-labelledby={qId}
    >
      {/* 🔴 UNCONDITIONAL. See the block above this function: `{q && …}` failed open and let the
          previous question's heading stand over these authorities. */}
      <p
        id={qId}
        className="cc-clark-sources-q"
        data-testid="clark-sources-q"
        data-q-absent={q ? "0" : "1"}
        title={q || undefined}
      >
        {q || "The question this answer belongs to wasn’t recorded."}
      </p>
      {/* The scope this turn was answered under, beside the question it answered. Same field the
          thread stamps (jurisdictions.js `scopeStampLabel`) — one value, two renderings. */}
      {scopeLabel && (
        <p className="cc-clark-sources-scope" data-testid="clark-sources-scope">{scopeLabel}</p>
      )}
      {/* The as-of line lives with the panel it dates. A treatment badge without a date is a claim
          about now; with one it is a claim about a moment, which is the only claim we can make. */}
      {checkedThrough && (
        <p className="cc-clark-sources-asof" data-testid="clark-sources-asof">
          {`Treatment checked through ${checkedThrough}`}
          {restored ? " — as it stood when this answer was given" : ""}
        </p>
      )}

      {authorities.length === 0 && seeAlso.length === 0 && unchecked.length === 0 ? (
        /* 🔴 A PENDING STATE IS A QUIET STATUS, NOT A GROUP HEADER. It used to be set in the same
           small-caps letterspaced treatment as the relied group's heading, so it read as a heading
           for a section that will never exist. Sentence case, italic, quiet. */
        <p className="cc-clark-sources-empty" data-testid="clark-sources-empty" data-pending={streaming ? "1" : "0"}>
          {streaming
            ? "Looking for authority…"
            : "This answer didn’t rest on any authority I could verify."}
        </p>
      ) : (
        <>
          {relied.length > 0 && (
            <>
              {/* ══ 🔴 RUN VC · CHANGE 1 — `Mentioned` → `RELIED ON`. ADAM'S CALL, TAKEN, 2026-08-25.
                  ⚠️ ON THE RECORD, BECAUSE IT IS NOT FREE. `clarkAnswerV2.js:243` sets `relied` from
                  prong A — `whySource === 'answer_prose' || (Number.isInteger(a.start) && a.start
                  >= 0)`. That is A MENTION AT AN OFFSET: the model named this authority somewhere
                  in its prose. It is not a checked support relation, and nothing in the pipeline
                  computes one today. Three independent populations agree on what the predicate
                  selects:

                      RUN PJ observed              18/19    94.7%
                      RUN PJ derived (lower bound) 115/129  89.1%
                      RUN PQ live-driven            76/85   89.4%
                      RUN VC, three captured turns   9/10   90.0%   (ops/run-vc/out/relied-fraction.json)

                  ⚠️ RUN VC's DENOMINATOR IS 10, NOT 16. Sixteen authorities are on the three
                  captured turns; six of them are on a turn captured BEFORE RUN PA and carrying no
                  `relied` and no `reliedKnown` at all. Counting those six as `relied: false` would
                  have reported 9/16 = 56% and made the label look far better earned than it is.
                  ⭐ A row where the field was never computed is not a row where it came out false —
                  which is BUNDLE C · DEFECT 3's rule, applied to this run's own arithmetic.

                  🔴 SO THIS HEADING SAYS `RELIED ON` OVER ROUGHLY NINE AUTHORITIES IN TEN ON THE
                  STRENGTH OF THE MODEL HAVING NAMED THEM. RUN RB moved the string the other way
                  for exactly that reason and Adam has now ruled it back. The ruling is taken and
                  the number is in the report; it becomes literally true when a support check
                  lands, and the predicate above is the one line that has to change when it does. */}
              <h3 className="cc-clark-sources-subhead" data-testid="clark-sources-subhead-relied">Relied on</h3>
              <ul className="cc-ans-panel-list cc-clark-sources-list" role="list"
                  aria-labelledby={qId} data-testid="clark-sources-relied">
                {relied.map((a) => (
                  <RailItem key={a.id} authority={a} onOpenResearch={onOpenResearch} />
                ))}
              </ul>
            </>
          )}
          {looked.length > 0 && (
            <>
              {/* 🔴 `ALSO RETRIEVED` → `RELATED AUTHORITIES`. Adam's ruling. One string.
                  The retired label described OUR PLUMBING — what the search happened to return.
                  "Related authorities" describes the reader's relationship to the law, which is
                  the only thing on this rail a lawyer has a use for. */}
              <h3 className="cc-clark-sources-subhead">Related authorities</h3>
              <ul className="cc-ans-panel-list cc-clark-sources-list" role="list"
                  aria-labelledby={qId} data-testid="clark-sources-looked">
                {looked.map((a) => (
                  <RailItem key={a.id} authority={a} onOpenResearch={onOpenResearch} />
                ))}
              </ul>
            </>
          )}
          {/* Retrieved, reliance not yet computed. No subhead when that is ALL there is: mid-stream
              the honest presentation is the cards and no claim about them. */}
          {unknown.length > 0 && (
            <>
              {known.length > 0 && <h3 className="cc-clark-sources-subhead">Retrieved so far</h3>}
              <ul className="cc-ans-panel-list cc-clark-sources-list" role="list"
                  aria-labelledby={qId} data-testid="clark-sources-pending">
                {unknown.map((a) => (
                  <RailItem key={a.id} authority={a} onOpenResearch={onOpenResearch} />
                ))}
              </ul>
            </>
          )}
          {/* ══ 🔴 RUN VC · CHANGE 2 — `SEE ALSO`: NAMED IN THE ANSWER, NOT CONFIRMED BY US. ══════
              Adam's change #2: the block RUN VB removed from under the answer comes back HERE, as
              a rail group.

              🔴🔴 RUN SH · SH-1 — IT IS **TWO** GROUPS NOW, AND VC's OWN SENTENCE IS WHY.
              VC wrote: *"It is `stripped` … and `unchecked` … together — two findings the envelope
              keeps apart and this group keeps apart in each row's `reason`, under one claim the
              rail is entitled to make about both: we could not confirm this."*

              The rail is not entitled to make one claim about both, because the rail does not
              print the `reason` — it prints ONE SENTENCE over the whole group, and VL's sentence
              for it is *"Named in this answer. Not found in our corpus, so we cannot vouch for
              it."* **True of `stripped`. False of `unchecked`.** `stripped` is a claim about THE
              LAW: we looked, and the corpus does not hold it. `unchecked` is a claim about US: our
              lookup did not complete, so we have neither confirmed nor ruled it out. Telling a
              lawyer a case was *not found in our corpus* when we never finished looking is the
              exact defect RUN 9 shipped live about *TransUnion LLC v. Ramirez*, **which is in the
              corpus** — reported absent because a redundant confirmation query was cancelled.

              RUN C and RUN S built the two-array separation to make that unsayable, and Research
              has honoured it since. Clark had not. One envelope, two apps, telling a lawyer
              different things about the same authority.

              ⚠️ THE `stripped` SENTENCE IS NOT SOFTENED — it is correct, and it is the claim that
              licenses that group. The defect was the fold, never the words.

              ⭐ AND THE TWO ARRAYS ARRIVE SEPARATE FROM `railPayload.js`. This component is not
              handed one list and asked to remember to split it; it is handed two, and a group that
              cannot be given the wrong rows cannot say the wrong sentence about them.

              🔴 GRAY, AND NOT CLICKABLE — BOTH GROUPS. Zero anchors and zero buttons inside,
              asserted by ops/run-vc/verify.mjs and ops/run-sh/harness/verify.mjs on the rendered
              DOM. There is nothing to open — we are not offering a document — and a control that
              opens nothing reads as broken rather than as inapplicable.

              🔴 AND NO BARE VOLUME AND PAGE, EVER, IN EITHER GROUP. `seeAlsoLabel` is the only
              thing that decides what a row may say; see `@citeclerk/ui/seeAlsoLabel` for the three
              shapes and RUN PJ's measurement for why. */}
          {seeAlso.length > 0 && (
            <>
              <h3 className="cc-clark-sources-subhead cc-clark-sources-subhead--quiet">See also</h3>
              {/* ══ 🔴 RUN VL — THE NOTE SITS ABOVE THE CARDS, AND IT IS A MEASUREMENT, NOT A TASTE.
                  It sat UNDER the group. Under a bare list that reads as a footnote to the list;
                  under CARDS it reads as a caption on the LAST CARD, which is the one thing this
                  sentence must never be — it is the claim that licenses the whole group, and
                  attached to one row it would leave the other rows making no claim at all.
                  MEASURED on VL's before arm at 1440x1100 (out/rail-before-wall-1440-precedent.json):
                  the note's top was y=1087 inside a rail whose box ends at y=1078 — the sentence
                  that qualifies three unverified authorities was BELOW THE FOLD while all three
                  were on screen. Above the cards it is read before them, which is the order the
                  claim has to arrive in. Both groups follow it. */}
              {/* 🔴 RUN CR · BLOCK 6 — RUN VL's NOTE IS DELETED. ADAM'S RULING: *"the text above
                  see also and the first card should be removed (about the corpus)."* It read
                  *"Named in this answer. Not found in our corpus, so we cannot vouch for it."*
                  VL's argument for putting it ABOVE the cards — that under CARDS it reads as a
                  caption on the last one, and that at 1440x1100 it measured BELOW THE FOLD at
                  y=1087 in a rail ending y=1078 — was right about placement and is now moot. It is
                  recorded so nobody restores it beneath the cards believing that is where it lived.
                  ⭐ Deleting it is also what makes the merge below lawful; see railPayload.js. */}
              <SeeAlsoCardList rows={seeAlso} qId={qId} listTestId="clark-sources-seealso"
                               cardTestId="clark-seealso-card" variant="seealso" />
            </>
          )}
          {/* ══ 🔴🔴 RUN CR · BLOCK 6 — THE `Could not be checked` GROUP IS DELETED. ═══════════
              ADAM'S RULING, two parts of one message: *"the text after the last see also card
              about what we cannot check should not be there"* and *"the cases listed should just
              be put into their own see also cards too."* The heading, the note and the separate
              list all came out; the ROWS are in `seeAlso` above, as cards, with source-type chips
              like every other card.

              WHAT STOOD HERE, AND WHY ITS ARGUMENT SURVIVES ITS CONTAINER. RUN SH · SH-1 built this
              group so an `unchecked` row could never fall under the "not found in our corpus"
              sentence — *"a group that cannot be handed the wrong rows cannot say the wrong
              sentence about them"* — because RUN 9 shipped exactly that live about *TransUnion LLC
              v. Ramirez*, **which is in the corpus**, reported absent because a confirmation query
              had been cancelled. SH-1 also corrected ITSELF here after measuring: its closing
              sentence was below the fold in 8 of 9 fixture × viewport combinations, so both halves
              were folded into one note above the cards.

              ⭐ THE SENTENCE THAT COULD BE WRONG IS THE ONE ADAM DELETED. With no note over the
              group, the merged list asserts nothing about any row, so there is nothing for an
              `unchecked` row to be mis-described BY. SH-1's hazard was the sentence; the split was
              its remedy, not its subject. Every row still carries `data-reason` and `data-group`,
              so the two kinds remain distinguishable in the DOM and to every probe — what is gone
              is the printed claim, which is what Adam ruled on. */}
        </>
      )}
    </section>
  );
}

/* ══ ⭐ RUN SH · SH-1 — ONE CARD LIST, TWO CALLERS. ══════════════════════════════════════════════
   Everything below this line is RUN VL's arrangement, unchanged in behaviour and moved into a
   component so that the `See also` group and the `Could not be checked` group cannot drift into
   rendering their rows differently. **The ONLY difference between the two groups is the prose
   above and below them**, which is the only thing that genuinely differs about the rows.

   🔴 EVERY ONE OF VL's REASONS IS LOAD-BEARING AND IS KEPT VERBATIM:

   🔴 THE SAMENESS IS STRUCTURAL, NOT COPIED. The `<li>` carries `cc-clark-sources-item` — the SAME
   class the relied/related/pending groups' items carry — so the 7px rhythm, the glassy
   `Case`/`Statute` chip and the rail's `--cc-shadow-md` arrive through the SAME rules, not through
   restated values. ⭐ Two cards that share a selector cannot drift; two cards that share a NUMBER
   drift the first time one of the numbers is edited.

   🔴 IT IS THE SHARED @citeclerk/ui CARD, AND packages/ui/react IS NOT EDITED. Every slot there is
   prop-guarded, so `cite`, `court`, `year`, `citedBy` and `why` are simply not passed and simply do
   not render. `git diff --stat HEAD -- packages/ui/react` is empty for this run; PJ's fence holds
   and Research's and Library's cards are untouched.

   🔴 NOT CLICKABLE, BY CONSTRUCTION RATHER THAN BY STYLING. No `href` and no `onSelect` sends the
   shared card down its `is-unlinked` branch — a plain `<div>`. There is no anchor to suppress and
   no button to disable, which is why the claim survives a stylesheet change.

   🔴 `unlinkedNote` IS DELIBERATELY WITHHELD. The resolved cards pass *"Not linked to an opinion
   record"* — a statement about a MISSING CLUSTER ID. These rows are not unlinked for want of an id,
   which the group's own note says once, correctly, for all of them. Two sentences giving two
   different reasons for one absence is how a reader learns to trust neither.

   🔴 AND THE NEVER-BARE RULE IS UNTOUCHED — `cite` IS NOT PASSED. `seeAlsoLabel` is still the only
   thing that decides what a row may say, and the card is handed its OUTPUT, never the row. ⭐ So
   these cards carry no meta line and are legitimately SHORTER than the relied cards, which do. That
   is the rule working, not a card left unfinished.

   🔴 A DISTINCT `cardTestId` PER GROUP, DELIBERATELY. Eleven probes in ops/ count
   `[data-testid="clark-authority-card"]` to mean "an authority this answer rests on", and a row in
   either of these groups is the opposite of that. The two groups also do not share a testId with
   each other — a probe that cannot tell them apart cannot assert the thing SH-1 is about. */
/* 🔴 RUN RQ · §1 — THE TWO VOICES, WORD-FOR-WORD THE ONES THE PROSE MARKERS ALREADY USE.
   `ClarkAnswerV2.jsx` titles its `unresolved` mark *"Named in the answer, but this authority did
   not resolve to a document in our corpus"* and its `unchecked` mark *"…We could not complete the
   lookup for this authority just now, so we have neither confirmed nor ruled it out."* The same
   authority marked in the prose and listed in the rail must not be described two different ways,
   so these are those sentences and not a third pair someone wrote later. */
const STRIPPED_TITLE = "Named in this answer. It did not resolve to a document in our corpus.";
const UNCHECKED_TITLE = "Named in this answer. We could not complete the lookup for it just now, so we have neither confirmed nor ruled it out.";
/* Clipped, not hidden — `.cc-sr-only` is `clip: rect(0 0 0 0)` on a 1x1 box, so a screen reader
   announces it and `innerText` does not carry it into a screenshot diff. Leading space so it is
   announced as a continuation of the citation, not as a second heading. */
const STRIPPED_SR = " — named in this answer, not found in our corpus";
const UNCHECKED_SR = " — named in this answer, we could not check it";

function SeeAlsoCardList({ rows, qId, listTestId, cardTestId, variant }) {
  return (
    <ul className={`cc-ans-panel-list cc-clark-sources-list cc-clark-sources-seealso cc-clark-sources-${variant}`}
        role="list" aria-labelledby={qId} data-testid={listTestId}>
      {rows.map((s) => {
        const label = seeAlsoLabel(s);
        /* Which of `seeAlsoLabel`'s three shapes this row took. Only `name` is an authority NAME;
           the other two are SENTENCES, and parts.jsx's rule 2 reserves serif italic for case names
           — setting a sentence in it would be a citation-format defect wearing the right font. */
        /* 🔴 RUN AC · BLOCK 6 — THE LABEL RETURNS ITS OWN SHAPE NOW, and this line is the
           reason it had to. `seeAlsoLabel` grew a fourth shape (`cite` — a statute's citation IS
           its name; see that module's header); this expression is a copy of its branch table and
           would have mapped that shape to `words`, setting a statute citation in the SENTENCE
           face. Clark is not this run's surface and takes no other change: one expression, so the
           shared rule and this call site cannot disagree about a row. */
        const shape = label.shape;
        return (
          <li
            key={s.key}
            className="cc-clark-sources-item cc-clark-sources-sa"
            data-kind={s.kind || undefined}
            data-shape={shape}
            data-cite-source={s.citeSource}
            data-reason={s.reason || undefined}
            /* 🔴 RUN CR · BLOCK 6 — THE ROW'S OWN GROUP, NOT THE LIST'S. The two kinds share one
               list now, so `variant` no longer distinguishes them; `s.group` (railPayload.js) does,
               and it is the only thing left in the DOM that says which of the two findings a row
               is. `variant` still names the list for styling and stays the fallback. */
            data-group={s.group || variant}
          >
            {/* ══ 🔴🔴 RUN RQ · §1 — THE APOLOGY IS GONE AND THE DISCLOSURE IS NOT. ════════════
                WHAT STOOD HERE, and what Adam read on the live panel:

                    The answer gave only a citation, “Fed. R. Civ. P. 56”, which we could not
                    confirm.

                *"That reads as incompetence and it goes. Render the citation plainly and
                confidently — bold, like the other citations, but in BLACK so it is visibly not
                clickable. No apology, no hedge, no sentence about what we could not do."*

                ⭐ THE RENDERING HE ASKED FOR WAS ALREADY BUILT AND ALREADY GATED SHUT.
                `seeAlsoLabel`'s `cite` shape (RUN AC) sets the citation in the NAME's face —
                13px, weight 400, `--cc-ink`, roman rather than italic because Indigo R2.1 reserves
                italic for case names — on the `--cc-bg-subtle` ground RUN AB gave this group, with
                no anchor and no hover. RUN RQ removed the `kind === 'statute'` gate; nothing about
                the treatment below changed. MEASURED, `--cc-ink` on `--cc-bg-subtle`: 16.99:1
                light, 15.96:1 dark, 14.17:1 gavel — AAA in all three, and higher than the accent a
                CLICKABLE name wears (9.69 / 8.71 / 5.28).

                🔴 THE `title` AND THE `.cc-sr-only` LINE ARE NOT DECORATION — THEY ARE THE DELETED
                SENTENCE'S JOB, REHOUSED. That sentence was the ONLY thing on this card telling a
                reader the row is unconfirmed; the grey ground and the black ink say it to someone
                who can see the resolved cards three inches away and cannot say it to anyone else.
                So the fact moves to where the prose markers already keep it (ClarkAnswerV2.jsx's
                `unresolved`/`unchecked` marks: a `title` plus clipped text), and Adam's ruling —
                remove the self-doubting PROSE — is satisfied without removing the disclosure.

                🔴 AND IT SPEAKS IN THE ROW'S OWN VOICE, WHICH IS RUN SH-1's WHOLE POINT. `stripped`
                is a claim about THE LAW (we looked; the corpus does not hold it); `unchecked` is a
                claim about US (our lookup did not finish). RUN 9 shipped the first sentence over an
                `unchecked` row and told a lawyer *TransUnion LLC v. Ramirez* was absent from a
                corpus that holds it. One printed sentence over a merged group is what made that
                possible, so this one is per-ROW, drawn from `s.group`, and cannot be said about the
                wrong half. */}
            <SharedAuthorityCard
              as="div"
              legacyClass="cc-ans-card"
              testId={cardTestId}
              data-variant={variant}
              title={s.group === "unchecked" ? UNCHECKED_TITLE : STRIPPED_TITLE}
              type={s.type}
              name={
                <>
                  {label.text}
                  <span className="cc-sr-only">
                    {s.group === "unchecked" ? UNCHECKED_SR : STRIPPED_SR}
                  </span>
                </>
              }
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * 🔴 STEP 4 — THE SOURCES COLUMN. The right-hand rail: what each answer relied on.
 *
 * Two things it does that the inline panel does not, and both are about the column's job:
 *
 *  1. IT SPLITS RELIED-ON FROM RETRIEVED. `clarkAnswerShape.js:104` already computes `relied` —
 *     whether any verified sentence actually cited this authority. The inline panel lists
 *     everything flat, which is right beneath prose; a column headed "Sources" that mixed the two
 *     would claim the answer rests on cases it merely looked at. The retrieved-but-unused ones are
 *     still SHOWN, under their own heading, because "here is what I looked at and did not use" is
 *     information and hiding it would be the more flattering lie.
 *
 *  2. IT HAS A REAL EMPTY STATE. An answer that relied on nothing gets a sentence saying so, not a
 *     blank rail. A column that renders nothing is indistinguishable from a column that broke —
 *     the same no-blanks rule ResearchAnswer.jsx is built on, applied to the rail.
 *     `clarkAuthorityParity.test.js` pins that by grepping this region for a bare null return —
 *     so do not write that phrase in prose here either, comments included. It has cost three
 *     assertions tonight (a raw hex in a CSS comment, a deleted identifier in a JS comment, this).
 *
 * 🔴 SOURCE CARDS LIVE ONLY HERE. Never in the thread. The old layout rendered them in both and
 * neither copy was anchored to a turn.
 */
/* ══ 🔴 RUN RB — THE MODAL STATE LIVES HERE, AND THAT IS THE REASON index.jsx IS NOT TOUCHED. ═══
   Clicking an authority NAVIGATED to Research and the Clark thread was gone — the answer being
   read, the question asked, the other authorities. Adam's ruling, 2026-08-24: bring the text up in
   a modal so the lawyer never leaves the Clark screen.

   ⭐ THE OPENED AUTHORITY IS RAIL STATE, SO IT IS HELD BY THE RAIL. Lifting it to index.jsx would
   have put a piece of the authority column's state in a file four other runs are holding, to no
   benefit: nothing outside this column opens this modal, and nothing outside it needs to know one
   is open. `SourcesColumn` already owns the turns, the split and the empty states.

   🔴 ⌘-CLICK AND MIDDLE-CLICK STILL GO TO RESEARCH, AND THAT IS NOT AN ACCIDENT OF THE SHARED
   CARD — IT IS WHY NO packages/ui EDIT WAS NEEDED. The shared card renders an `<a href>` and its
   onSelect handler does `e.preventDefault(); onSelect(e)` (react/AuthorityCard.jsx:166). A PLAIN
   click is intercepted and opens the modal; a modified click is the browser's and still opens the
   full reader in a new tab. Both behaviours come free from the component as it already stands, so
   Research's surface is untouched — which is the fence RUN PJ established and this run keeps. */
export function SourcesColumn({ turns = [], focusTurnId = null, onOpenResearch }) {
  const [openAuthority, setOpenAuthority] = useState(null);
  /* `onOpenResearch` is still accepted and still forwarded for any caller that wants the old
     navigation; the rail itself no longer uses it, because the modal IS the destination now and
     the modal carries its own `Open in Research`. */
  const openInModal = useCallback((authority) => setOpenAuthority(authority), []);
  const closeModal = useCallback(() => setOpenAuthority(null), []);

  /* ══ 🔴 RUN VC · CHANGES 5 AND 6 — WHICH TURN IS ON THE RAIL. ════════════════════════════════
     ONE expression, and every rule Adam gave is a term in it:

       · **the newest by default** — `turns` arrives in thread order (index.jsx passes
         `turns.filter((t) => t.answer)` and does not sort), so the newest answered turn is the
         LAST element. Taking the last is what puts the newest query's sources at the top of the
         rail; with one block rendered, "at the top" needs no scroll to be true.
       · **one query at a time** — one block is selected here and one block is rendered below.
       · **`Show Authorities` re-points the rail** — `focusTurnId` is that control's landing
         point. A host that passes it owns the choice; a host that passes nothing gets the newest.

     🔴 IT FALLS BACK TO THE NEWEST WHENEVER THE ID IS NOT ON THE RAIL, NEVER TO AN INDEX. Bundle C
     established this and the reason has not changed: a new chat, a reload or a discarded
     placeholder makes an id stale, and an index would then point at a DIFFERENT answer rather than
     at none. An id that no longer matches anything is not an error state — it is the newest answer.

     ⭐ AND IT FOLLOWS THE NEWEST ANSWER BY CONSTRUCTION, WITH NO EFFECT AND NO STORED STATE. This
     component holds no selection of its own. When the user asks another question the host either
     re-points `focusTurnId` at it or passes nothing, and either way the very next render shows the
     new turn. Lane 2's warning — *a rail you must navigate to match the thread is a rail that can
     disagree with the thread* — is answered by having nothing here that can go stale. */
  const shown = turns.find((t) => t.id === focusTurnId) || turns[turns.length - 1] || null;

  return (
    <>
    {/* 🔴 STREAM C — "SOURCES" BECOMES "AUTHORITIES", AND THE TREATMENT COMES FROM RESEARCH.
       Three divergences, per the audit: the WORD (Clark said `SOURCES`, Research says
       `AUTHORITIES`), the SIZE (12px vs 16px) and the CASING (uppercase +0.72px vs sentence).
       Unified on Research's on all three. The word matters most: a "source" is anything you read,
       an AUTHORITY is something you can cite, and this rail lists the second.

       🔴 RUN RF, 2026-08-25 — THE BRACES AROUND THIS COMMENT ARE LOAD-BEARING. DO NOT REMOVE THEM.
       This block sat here BARE for the life of RUN RB's commit and painted 700px of itself onto the
       Clark screen. It was written when `return (` opened directly onto `<aside>`, where a bare
       block comment sits in JS EXPRESSION position and is therefore a comment. RB wrapped the return
       in a fragment to hang the modal off it as a sibling — correct, and it silently moved this
       block into JSX CHILDREN position, where a bare block comment is not syntax at all. It is TEXT.
       React rendered it, `.cc-clark-main` made it an anonymous flex item, and the stage —
       `flex: 1 1 auto; min-width: 0` — gave up 703px of an 880px box to it.
       ⭐ WRAPPING JSX IN A FRAGMENT RE-TYPES EVERY BARE COMMENT INSIDE IT FROM CODE TO CONTENT.
       ⚠️ And note what this comment may not contain: the two characters that CLOSE a block comment.
       RF's own first patch spelled them out here as an example and truncated this comment at that
       example — caught by ops/run-rf/sweep-jsx-text.mjs, which is the third assertion this one
       file's header says the phrase has cost. Say `star-slash`; never type it. */}
    <aside className="cc-clark-sources" aria-label="Authorities" data-testid="clark-sources-column">
      <h2 className="cc-clark-sources-head">Authorities</h2>
      {/* 🔴 RUN VC — ONE BLOCK. `shown` is the newest answered turn unless a host re-points the
          rail at another one. The `key` is the turn id, deliberately: switching turns REMOUNTS the
          block rather than re-using it, so no piece of one question's block can survive into
          another's. That is cheap here (the block is markup with no state of its own) and it is
          the structural half of defect zero's fix — the half that does not depend on any field
          being present in the payload. */}
      {!shown ? (
        <p className="cc-clark-sources-empty">No authorities yet.</p>
      ) : (
        <SourcesTurn key={shown.id} turn={shown} onOpenResearch={openInModal} />
      )}
      </aside>
      {/* Rendered as a SIBLING of the rail, not inside a card: `Modal` portals to <body>, so the
          modal is never clipped by the rail's own scroll container or its overflow. ⚠️ That also
          matters for the BLUR — `backdrop-filter` needs a paint layer beneath it and can silently
          no-op inside a transformed or clipped ancestor. */}
      <AuthorityModal authority={openAuthority} onClose={closeModal} />
    </>
  );
}

export default AuthorityCard;
